import keys from '@debbie/common/dist/cache/keys.js'
import { getJournalNumber } from '@debbie/common/dist/cases/index.js'
import { VoucherTypeIds } from '@debbie/common/dist/economy/vouchers/index.js'
import {
  EmbedPlacement,
  getEmbedAreas,
} from '@debbie/common/dist/embeds/index.js'
import { Entity } from '@debbie/common/dist/index.js'
import { InteractionType } from '@debbie/common/dist/interactions/index.js'
import { Direction } from '@debbie/common/dist/interactions/index.js'
import * as updatesLib from '@debbie/cortex/dist/api/updates.js'
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js'
import { useCase } from '@debbie/cortex/dist/cases/index.js'
import { useCustomer } from '@debbie/cortex/dist/customers/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { useRole } from '@debbie/cortex/dist/me/index.js'
import q from '@debbie/cortex/dist/query.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { useTenant } from '@debbie/cortex/dist/tenant/index.js'
import { useUpdateTypesMap } from '@debbie/cortex/dist/update-types/index.js'
import {
  updatesEntity,
  useUpdatesByRefId,
} from '@debbie/cortex/dist/updates/index.js'
import CasePauseStatus, {
  getActivePauses,
} from '@debbie/iris/dist/components/CasePauseStatus/index.js'
import Documents from '@debbie/iris/dist/components/documents/Documents.js'
import CaseVouchers from '@debbie/iris/dist/components/economy/case-vouchers/CaseVouchers.js'
import { DecoratedVoucher } from '@debbie/iris/dist/components/economy/case-vouchers/types.js'
import CreateVoucher from '@debbie/iris/dist/components/economy/CreateVoucher/index.js'
import { Col, Row } from '@debbie/iris/dist/components/Grid/index.js'
import CaseHistory from '@debbie/iris/dist/components/Hist/CaseHistory.js'
import {
  ButtonDrop,
  CaseInfo,
  CaseStatus,
  CopyToClipboard,
  EmbedsFrame,
  MenuItem,
  PageMenu,
  Panel,
  PlaceholderLoader,
  Properties,
} from '@debbie/iris/dist/components/index.js'
import { NoteInteraction } from '@debbie/iris/dist/components/interactions/index.js'
import WorkflowStatus from '@debbie/iris/dist/components/WorkflowStatus.js'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'

import CreditorBillingModal from './CreditorBillingModal.js'

const CaseSummary = (props: { caseId: string }) => {
  const { caseId } = props

  const singleCaseLoader = useCase(caseId)
  const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null
  const customerLoader = useCustomer(singleCase?.customerId)
  const customer = h.hasData(customerLoader) ? customerLoader.data : null

  const updatesLoadable = useUpdatesByRefId(singleCase?.caseId)
  const caseUpdates = h.hasData(updatesLoadable) ? updatesLoadable.data : null
  const updateTypesMapLoadable = useUpdateTypesMap()
  const updateTypesMap = h.hasData(updateTypesMapLoadable)
    ? updateTypesMapLoadable.data
    : {}
  const caseVouchers_ = useCaseVouchersByCase(singleCase?.caseId)
  const _caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null
  const meRole = useRole()
  const tenant = useTenant()
  const lang = useSelector((s: RootState) => s.lang)
  const [addVoucherOpen, setAddVoucherOpen] = useState(false)

  const continueCase = async () => {
    const awaitingClientPauses = getActivePauses(
      caseUpdates,
      updateTypesMap,
      meRole,
    ).filter((update) => update.isAwaitingClient)

    if (awaitingClientPauses) {
      const promises = awaitingClientPauses.map((pause) =>
        updatesLib.patch(pause.update.updateId),
      )
      await Promise.all(promises)
      q.invalidate(
        awaitingClientPauses.flatMap((pause) =>
          keys.write.modify(updatesEntity, pause.update.updateId),
        ),
      )
    }
  }

  const embedAreas = tenant
    ? getEmbedAreas(tenant.embeds, EmbedPlacement.CreditorWebCasePage)
    : []

  const caseVouchers =
    _caseVouchers &&
    _caseVouchers.map((caseVoucher): DecoratedVoucher => {
      return { ...caseVoucher, children: [] }
    })

  const journalNumber = singleCase
    ? singleCase?.customJournalNumber
      ? getJournalNumber(singleCase?.customJournalNumber, singleCase?.id)
      : `${singleCase?.id}`
    : ''

  return (
    <Row>
      <Col size="md" width="12">
        <PageMenu
          breadCrumb={[
            {
              text: lang.navigation.cases,
              path: '/cases',
            },
            {
              text: journalNumber,
              path: `/cases/${singleCase?.id}`,
              copyToClipBoard: journalNumber,
            },
          ]}
          contentLeft={
            <>
              {singleCase && singleCase.workflowId && customer && (
                <MenuItem>
                  <WorkflowStatus case={singleCase} customer={customer} />
                </MenuItem>
              )}
            </>
          }
          contentRight={
            singleCase && (
              <>
                <MenuItem>
                  <CaseStatus
                    status={singleCase.status}
                    statusTime={singleCase.statusTime}
                    endReasonId={singleCase.endReasonId}
                  />
                </MenuItem>
              </>
            )
          }
        />
      </Col>
      <Col size="md" width="12">
        {singleCase && <CasePauseStatus case={singleCase} />}
      </Col>
      <Col size="md" width="5">
        <Panel
          header={
            <>
              <CopyToClipboard text={singleCase && singleCase.caseId} hideIcon>
                {lang.cases.caseInfo}
              </CopyToClipboard>
            </>
          }
          bodyStyle={{
            minHeight: '300px',
          }}
        >
          <CaseInfo caseId={caseId} />
        </Panel>
        <Panel
          header={
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {lang.economy.caseEconomy}
              <ButtonDrop
                open={addVoucherOpen}
                onOpen={() => {
                  setAddVoucherOpen(true)
                }}
                onClose={() => {
                  setAddVoucherOpen(false)
                }}
                size="mini"
              >
                {singleCase && (
                  <CreateVoucher
                    caseId={singleCase.caseId}
                    creditorId={singleCase.creditorId}
                    voucherTypeFilter={[VoucherTypeIds.PRINCIPAL]}
                    onCreate={() => {
                      setAddVoucherOpen(false)
                    }}
                    caseVouchers={caseVouchers}
                    singleCase={singleCase}
                  />
                )}
              </ButtonDrop>
            </div>
          }
        >
          {singleCase && caseVouchers ? (
            <>
              <CaseVouchers
                singleCase={singleCase}
                caseVouchers={caseVouchers}
              />

              <hr />
              <CreditorBillingModal
                tenantId={singleCase.tenantId}
                caseId={singleCase.caseId}
                creditorId={singleCase.creditorId}
                currency={singleCase.currency}
              />
            </>
          ) : (
            <PlaceholderLoader />
          )}
        </Panel>
        {embedAreas &&
          singleCase &&
          embedAreas.map((embedArea) => (
            <Panel
              key={embedArea.embedId}
              header={embedArea.title}
              bodyStyle={{ overflow: 'hidden' }}
            >
              <div
                style={{
                  margin: '-20px -20px -25px -20px',
                }}
              >
                <EmbedsFrame
                  id={embedArea.embedId}
                  height={
                    embedArea.placement === EmbedPlacement.CreditorWebCasePage
                      ? embedArea.details.height
                      : undefined
                  }
                  context={{
                    caseId: singleCase.caseId,
                  }}
                />
              </div>
            </Panel>
          ))}
        {singleCase && !tenant.settings.hideDocumentsFromCreditors && (
          <Documents
            case={singleCase}
            entityId={singleCase.caseId}
            entityType={Entity.Case}
          />
        )}
        {singleCase && singleCase.caseGroup && (
          <Panel header={lang.properties.properties}>
            <Properties
              propRef={Entity.Case}
              propRefId={singleCase.caseId}
              caseGroupId={singleCase.caseGroup.id}
              editable={false}
            />
          </Panel>
        )}
      </Col>
      <Col size="md" width="7">
        <Panel header={lang.cases.sendNoteToCollector}>
          {singleCase && (
            <NoteInteraction
              referenceId={singleCase.caseId}
              referenceType={Entity.Case}
              id={singleCase.id}
              options={{
                direction: Direction.INCOMING,
                type: InteractionType.ClientNote,
              }}
              sendButtonText={lang.interactions.send}
              continueCase={continueCase}
            />
          )}
        </Panel>
        {singleCase && <CaseHistory caseId={singleCase.caseId} />}
      </Col>
    </Row>
  )
}

export default CaseSummary
